import React, { useEffect } from 'react'

import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { datadogRum } from '@datadog/browser-rum'
import { LicenseManager } from 'ag-grid-enterprise'
import LogRocket from 'logrocket'
import { AppProps } from 'next/app'
import { Inter } from 'next/font/google'
import { useRouter } from 'next/router'
import NProgress from 'nprogress'
import { Provider } from 'react-redux'
import { ToastContainer } from 'react-toastify'

import store from '@/client/store'
import ErrorBoundary from '@/components/ErrorBoundary'
import { msalConfig } from '@/services/microsoftAuth'
import '@/styles/globals.css'

const inter = Inter({ subsets: ['latin'] })

function MyApp({ Component, pageProps }: AppProps) {
  const router = useRouter()
  const logRocket = process.env.NEXT_PUBLIC_OPS_APP_LOG_ROCKET_PROJECT
  const datadog = process.env.NEXT_PUBLIC_DATADOG_APP_ID

  const msalInstance = new PublicClientApplication(msalConfig)

  useEffect(() => {
    if (logRocket) {
      LogRocket.init(process.env.NEXT_PUBLIC_OPS_APP_LOG_ROCKET_PROJECT)
    }

    if (datadog) {
      datadogRum.init({
        applicationId: process.env.NEXT_PUBLIC_DATADOG_APP_ID,
        clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
        site: process.env.NEXT_PUBLIC_DATADOG_SITE,
        service: process.env.NEXT_PUBLIC_DATADOG_SERVICE,
        env: process.env.NEXT_PUBLIC_DATADOG_ENVIRONMENT,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100, // if not included, the default is 100
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
      })
      datadogRum.startSessionReplayRecording()
    }

    LicenseManager.setLicenseKey(process.env.NEXT_PUBLIC_AG_GRID_LICENSE_KEY)
  }, [])

  useEffect(() => {
    const handleStart = () => NProgress.start()
    const handleStop = () => NProgress.done()

    router.events.on('routeChangeStart', handleStart)
    router.events.on('routeChangeComplete', handleStop)
    router.events.on('routeChangeError', handleStop)

    return () => {
      router.events.off('routeChangeStart', handleStart)
      router.events.off('routeChangeComplete', handleStop)
      router.events.off('routeChangeError', handleStop)
    }
  }, [router])

  return (
    <Provider store={store}>
      <MsalProvider instance={msalInstance}>
        <ErrorBoundary>
          <main className={inter.className}>
            <Component {...pageProps} />
          </main>
        </ErrorBoundary>
        <ToastContainer
          containerId='global'
          limit={3}
        />
      </MsalProvider>
    </Provider>
  )
}

export default MyApp
